import React from 'react';
import { SemanticGPUCardProps } from '../../types/gpu';

const CATEGORY_COLORS = {
  'flagship': 'bg-gradient-to-r from-purple-600 to-pink-600',
  'training_premium': 'bg-gradient-to-r from-blue-600 to-indigo-600',
  'training_standard': 'bg-gradient-to-r from-green-600 to-teal-600',
  'training_entry': 'bg-gradient-to-r from-cyan-600 to-blue-500',
  'inference_optimized': 'bg-gradient-to-r from-orange-600 to-red-600',
  'cost_optimized': 'bg-gradient-to-r from-yellow-600 to-orange-600',
  'legacy': 'bg-gradient-to-r from-gray-600 to-gray-700',
  'specialized': 'bg-gradient-to-r from-emerald-600 to-green-600'
};

const TIER_COLORS = {
  'ultra_high': 'text-purple-400',
  'high': 'text-blue-400',
  'medium': 'text-green-400',
  'low': 'text-yellow-400'
};

const COST_COLORS = {
  'budget': 'text-green-400',
  'medium': 'text-yellow-400',
  'high': 'text-orange-400',
  'premium': 'text-red-400'
};

const getProviderIcon = (provider: string) => {
  const className = "w-5 h-5";

  switch (provider.toLowerCase()) {
    case 'aws':
      return <img src="/icons/icons8-aws-48.svg" alt="AWS" className={className} />;
    case 'gcp':
      return <img src="/icons/icons8-google-cloud-48.svg" alt="Google Cloud" className={className} />;
    case 'oci':
      return <img src="/icons/ORCL_logo.svg" alt="Oracle Cloud" className={className} />;
    case 'coreweave':
      return <img src="/icons/CoreWeave_full_text.png" alt="CoreWeave" className={className} />;
    case 'azure':
      return <img src="/icons/azure.svg" alt="Microsoft Azure" className={className} />;
    default:
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="#6366F1" />
        </svg>
      );
  }
};

const getFreshnessIndicator = (freshness: string) => {
  switch (freshness) {
    case 'fresh':
      return <div className="w-2 h-2 bg-green-500 rounded-full" title="Fresh data" />;
    case 'stale':
      return <div className="w-2 h-2 bg-yellow-500 rounded-full" title="Stale data" />;
    case 'very_stale':
      return <div className="w-2 h-2 bg-red-500 rounded-full" title="Very stale data" />;
    default:
      return <div className="w-2 h-2 bg-gray-500 rounded-full" title="Unknown data age" />;
  }
};

export default function SemanticGPUCard({ gpu, onLaunch, onViewDetails }: SemanticGPUCardProps) {
  const categoryColor = CATEGORY_COLORS[gpu.performance_category as keyof typeof CATEGORY_COLORS] || 'bg-gray-600';
  const memoryColor = TIER_COLORS[gpu.memory_tier as keyof typeof TIER_COLORS] || 'text-gray-400';
  const costColor = COST_COLORS[gpu.cost_tier as keyof typeof COST_COLORS] || 'text-gray-400';

  const topUseCase = Object.entries(gpu.use_case_scores)
    .sort(([,a], [,b]) => b - a)[0];

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 transition-all duration-200 hover:shadow-lg">
      {/* Header with category gradient */}
      <div className={`${categoryColor} p-4 rounded-t-xl`}>
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            {getProviderIcon(gpu.provider)}
            <div>
              <h3 className="font-semibold text-lg">{gpu.display_name}</h3>
              <p className="text-sm opacity-90">{gpu.short_description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getFreshnessIndicator(gpu.data_freshness)}
            <div className="text-right">
              <div className="text-2xl font-bold">
                {Math.round(gpu.performance_score)}
              </div>
              <div className="text-xs opacity-80">Score</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Technical specs row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-400 text-xs mb-1">GPUs</div>
            <div className="text-white font-medium">
              {gpu.gpu_count}x GPU
            </div>
          </div>
          <div>
            <div className="text-gray-400 text-xs mb-1">VRAM</div>
            <div className={`font-medium ${memoryColor}`}>
              {gpu.total_gpu_memory_gb ? `${gpu.total_gpu_memory_gb} GB` : 'N/A'}
            </div>
            {gpu.system_memory_gib != null && (
              <div className="text-xs text-gray-500">{gpu.system_memory_gib} GiB RAM</div>
            )}
          </div>
          <div>
            <div className="text-gray-400 text-xs mb-1">vCPUs</div>
            <div className="text-white font-medium">
              {gpu.vcpus || 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-gray-400 text-xs mb-1">Region</div>
            <div className="text-white font-medium text-xs">
              {gpu.region}
            </div>
          </div>
        </div>


        {/* Pricing */}
        <div className="flex items-center justify-between bg-gray-800/30 rounded-lg p-3">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-gray-400 text-xs">On-Demand</div>
              <div className="text-white font-bold">
                {gpu.total_price_per_hour
                  ? `$${gpu.total_price_per_hour.toFixed(2)}/hr`
                  : gpu.on_demand_price_per_hour
                    ? `$${gpu.on_demand_price_per_hour.toFixed(2)}/hr`
                    : 'N/A'
                }
              </div>
              {gpu.disk_price_per_hour != null && (
                <div className="text-gray-500 text-xs">incl. {gpu.default_disk_size_gb}GB disk</div>
              )}
              {gpu.price_stale && (
                <div
                  className="text-amber-400 text-xs mt-0.5"
                  title={`Price last refreshed: ${gpu.price_last_updated ?? 'unknown'}`}
                >
                  Price may be outdated
                </div>
              )}
            </div>
            {gpu.spot_price_per_hour && (
              <div>
                <div className="text-gray-400 text-xs">Spot</div>
                <div className="text-green-400 font-bold">
                  ${gpu.spot_price_per_hour.toFixed(2)}/hr
                </div>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-gray-400 text-xs">Cost Tier</div>
            <div className={`font-medium capitalize ${costColor}`}>
              {gpu.cost_tier}
            </div>
          </div>
        </div>

        {/* Recommended workloads */}
        {gpu.recommended_workloads.length > 0 && (
          <div>
            <div className="text-gray-400 text-xs mb-2">Best For</div>
            <div className="flex flex-wrap gap-2">
              {gpu.recommended_workloads.slice(0, 3).map((workload, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-600/20 text-blue-400 rounded-md text-xs"
                >
                  {workload}
                </span>
              ))}
              {gpu.recommended_workloads.length > 3 && (
                <span className="px-2 py-1 bg-gray-600/20 text-gray-400 rounded-md text-xs">
                  +{gpu.recommended_workloads.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Top use case confidence */}
        {topUseCase && topUseCase[1] > 0.6 && (
          <div className="bg-green-600/10 border border-green-600/20 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <span className="text-green-400 text-sm font-medium">
                Optimized for {topUseCase[0].replace(/_/g, ' ')}
              </span>
              <span className="text-green-400 text-xs font-bold">
                {Math.round(topUseCase[1] * 100)}% match
              </span>
            </div>
          </div>
        )}

        {/* Quota context — remaining is always in launchable instance count */}
        {(gpu.quota_group_label || gpu.quota_remaining != null) && (() => {
          const remaining = gpu.quota_group_remaining ?? gpu.quota_remaining;
          const isLimited = remaining != null && remaining <= 2 && remaining > 0;
          const isStale = gpu.quota_stale === true;
          return (
            <div className="text-xs text-gray-400 flex items-center gap-2 flex-wrap">
              {gpu.quota_group_label ? (
                <span>
                  {gpu.quota_group_remaining ?? '?'} launchable ({gpu.quota_group_label})
                </span>
              ) : gpu.quota_remaining != null ? (
                <span>
                  {gpu.quota_remaining} launchable
                </span>
              ) : null}
              {isLimited && (
                <span className="text-amber-400 font-medium">Limited availability</span>
              )}
              {isStale && (
                <span className="text-gray-500" title={`Quota last checked: ${gpu.quota_last_checked}`}>
                  (may have changed)
                </span>
              )}
            </div>
          );
        })()}

        {/* Zone capacity warning */}
        {gpu.capacity_warning && (
          <div className="flex items-center gap-1.5 px-2 py-1.5 bg-amber-900/30 border border-amber-700/40 rounded text-xs text-amber-400">
            <span className="shrink-0">&#9888;</span>
            <span>{gpu.capacity_warning}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={() => {
              if (gpu.capacity_constrained) {
                if (!window.confirm('This GPU type has reported capacity issues in this region. Launch may fail. Continue?')) {
                  return;
                }
              }
              onLaunch(gpu);
            }}
            className={`flex-1 text-white px-4 py-2 rounded-lg font-medium transition-colors ${
              gpu.capacity_constrained
                ? 'bg-amber-700 hover:bg-amber-600'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {gpu.capacity_constrained ? 'Limited Capacity' : 'Launch'}
          </button>
          <button
            onClick={() => onViewDetails(gpu)}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
          >
            Details
          </button>
        </div>

        {/* Technical identifier (small) */}
        <div className="text-xs text-gray-500 pt-1 border-t border-gray-700">
          {gpu.provider_instance_id}
        </div>
      </div>

    </div>
  );
}